import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const EmailTracking = () => {
  const [startupExplanation, setStartupExplanation] = useState(null);
  const [sendingPlan, setSendingPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [activeTab, setActiveTab] = useState('explanation');

  useEffect(() => {
    fetchStartupExplanation();
  }, []);

  const fetchStartupExplanation = async () => {
    try {
      const response = await fetch('/api/v1/email-tracking/startup-explanation');
      if (response.ok) {
        const data = await response.json();
        setStartupExplanation(data);
      } else {
        console.error('Failed to fetch startup explanation');
      }
    } catch (error) {
      console.error('Error fetching startup explanation:', error);
      toast.error('Failed to load agent startup explanation');
    } finally {
      setLoading(false);
    }
  };

  const fetchSendingPlan = async () => {
    setLoadingPlan(true);
    try {
      const response = await fetch('/api/v1/email-tracking/sending-plan');
      if (response.ok) {
        const data = await response.json();
        setSendingPlan(data);
        toast.success('Generated detailed sending plan!');
      } else {
        toast.error('Failed to generate sending plan');
      }
    } catch (error) {
      console.error('Error fetching sending plan:', error);
      toast.error('Failed to generate sending plan');
    } finally {
      setLoadingPlan(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${Math.round(minutes)} minutes`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 mt-20">
        <div className="loading-spinner mr-2"></div>
        <span>Loading email tracking information...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 mt-20">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">📊 Email Tracking & Agent Overview</h1>
        <p className="text-gray-500 text-sm sm:text-base">Detailed visibility into email sending, timing, and agent behavior</p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('explanation')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'explanation'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🤖 Agent Explanation
            </button>
            <button
              onClick={() => setActiveTab('plan')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'plan'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📅 Email Sending Plan
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'explanation' && startupExplanation && (
            <div className="space-y-6">
              {/* Overview */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h2 className="text-lg font-semibold text-blue-900 mb-2">{startupExplanation.title}</h2>
                <p className="text-blue-800">{startupExplanation.overview}</p>
              </div>

              {/* Agents Details */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {startupExplanation.agents_started?.map((agent, index) => (
                  <div key={`agent-${agent.name || index}`} className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{agent.name}</h3>
                    <p className="text-gray-600 mb-3">{agent.description}</p>
                    
                    <h4 className="font-medium text-gray-900 mb-2">Actions:</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {agent.actions?.map((action, actionIndex) => (
                        <li key={`action-${index}-${actionIndex}-${action.substring(0, 20)}`} className="flex items-start">
                          <span className="text-blue-500 mr-2 mt-1">•</span>
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* System Behavior */}
              {startupExplanation.system_behavior && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">🔄 System Behavior</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Startup Sequence:</h4>
                      <ol className="space-y-1 text-sm text-gray-600">
                        {startupExplanation.system_behavior.startup_sequence?.map((step, index) => (
                          <li key={`startup-step-${index}-${step.substring(0, 20)}`}>{step}</li>
                        ))}
                      </ol>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <span className="font-medium text-gray-900">Decision Making: </span>
                        <span className="text-gray-600 text-sm">{startupExplanation.system_behavior.decision_making}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Human Simulation: </span>
                        <span className="text-gray-600 text-sm">{startupExplanation.system_behavior.human_simulation}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Adaptability: </span>
                        <span className="text-gray-600 text-sm">{startupExplanation.system_behavior.adaptability}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'plan' && (
            <div className="space-y-6">
              {/* Generate Plan Button */}
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">📧 Detailed Email Sending Plan</h2>
                <button
                  onClick={fetchSendingPlan}
                  disabled={loadingPlan}
                  className="btn-primary flex items-center space-x-2"
                >
                  {loadingPlan ? (
                    <>
                      <div className="loading-spinner-small"></div>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Generate New Plan</span>
                    </>
                  )}
                </button>
              </div>

              {sendingPlan && (
                <div className="space-y-6">
                  {/* Plan Overview */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-700">{sendingPlan.total_emails_planned}</div>
                      <div className="text-sm text-blue-600">Total Emails</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-700">{sendingPlan.total_accounts}</div>
                      <div className="text-sm text-green-600">Email Accounts</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-purple-700">
                        {sendingPlan.recipient_breakdown?.total_unique_recipients || 0}
                      </div>
                      <div className="text-sm text-purple-600">Unique Recipients</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-orange-700">
                        {sendingPlan.timing_details ? formatDuration(sendingPlan.timing_details.total_duration_minutes) : 'N/A'}
                      </div>
                      <div className="text-sm text-orange-600">Total Duration</div>
                    </div>
                  </div>

                  {/* Timing Details */}
                  {sendingPlan.timing_details && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">⏰ Timing Analysis</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Earliest Send: </span>
                          <span>{formatDateTime(sendingPlan.timing_details.earliest_send_time)}</span>
                        </div>
                        <div>
                          <span className="font-medium">Latest Send: </span>
                          <span>{formatDateTime(sendingPlan.timing_details.latest_send_time)}</span>
                        </div>
                        <div>
                          <span className="font-medium">Business Hours Compliant: </span>
                          <span className={sendingPlan.timing_details.business_hours_compliant?.all_compliant ? 'text-green-600' : 'text-red-600'}>
                            {sendingPlan.timing_details.business_hours_compliant?.all_compliant ? '✅ Yes' : '❌ No'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Human Behavior Score: </span>
                          <span className="text-green-600">{sendingPlan.timing_details.estimated_human_behavior_score}/100</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Account-by-Account Schedule */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">📋 Account-by-Account Schedule</h3>
                    <div className="space-y-4">
                      {sendingPlan.detailed_schedule?.map((accountSchedule, index) => (
                        <div key={`account-${accountSchedule.account_email || index}`} className="border border-gray-200 rounded-lg p-4">
                          {/* Account Header */}
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3">
                            <div>
                              <h4 className="font-medium text-gray-900">{accountSchedule.account_email}</h4>
                              <p className="text-sm text-gray-600">
                                {accountSchedule.account_domain} • {accountSchedule.account_isp} • 
                                {accountSchedule.emails_planned} emails ({accountSchedule.agent_decision})
                              </p>
                            </div>
                            <div className="text-sm text-gray-600 mt-2 sm:mt-0">
                              <div>Start: {formatDateTime(accountSchedule.scheduled_start_time)}</div>
                              <div>End: {formatDateTime(accountSchedule.estimated_completion_time)}</div>
                            </div>
                          </div>

                          {/* Email Details */}
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Scheduled Time</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Agent Delay</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expected Open</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {accountSchedule.email_details?.map((email, emailIndex) => (
                                  <tr key={`email-${email.recipient_email}-${email.email_sequence || emailIndex}`} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{email.email_sequence}</td>
                                    <td className="px-3 py-2 text-sm text-gray-900">
                                      <div>{email.recipient_email}</div>
                                      <div className="text-xs text-gray-500">{email.recipient_company}</div>
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-900">{email.estimated_subject}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                                      {formatDateTime(email.scheduled_time)}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-600">
                                      <div className="flex items-center">
                                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2">
                                          {email.intelligent_delay || 'N/A'}
                                        </span>
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        {email.agent_delay_decision || 'Agent-calculated'}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                                      {formatDateTime(email.expected_open_time)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recipient Breakdown */}
                  {sendingPlan.recipient_breakdown && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">📊 Recipients by Domain</h4>
                        <div className="space-y-2">
                          {Object.entries(sendingPlan.recipient_breakdown.by_domain || {}).map(([domain, count]) => (
                            <div key={domain} className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">{domain}</span>
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">🏢 Recipients by Company</h4>
                        <div className="space-y-2">
                          {Object.entries(sendingPlan.recipient_breakdown.by_company || {}).map(([company, count]) => (
                            <div key={company} className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">{company}</span>
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!sendingPlan && (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No sending plan generated yet</h3>
                  <p className="text-gray-500 mb-4">Click "Generate New Plan" to see detailed email scheduling</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTracking;
